/**
 * ISSUE-1039. A public pool found in pool search previews and joins by
 * `targetPoolId` (ISSUE-1038), so a fan who is not in it sees the pool and a
 * Join pool button instead of the pool screen's no-access state.
 *
 * Error handling splits by step, the same split `JoinPoolDialog.tsx` makes:
 * a refused preview renders inline, since it is an expected answer to a
 * lookup; a refused join goes through the shared error modal, since a
 * committed action failed.
 *
 * The route mounts this only while it is open, with `key={poolId}`, so every
 * open starts from fresh state and there is no reset logic here.
 */
import { usePoolJoin, usePoolJoinPreview } from "@sportsmet/pool-sdk/private";
import { useLeagueCatalogGet } from "@sportsmet/sports-sdk/private";
import { Button, Dialog, InlineBanner } from "@sportsmet/ui";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { showApiError, showOfflineError } from "../../errors/apiErrorBus";
import { JOIN_OFFLINE_MESSAGE } from "./joinPoolErrorCopy";
import { newJoinRequestId } from "./joinPoolFlow";
import { JoinPoolPreview, JoinPoolPreviewSkeleton } from "./JoinPoolPreview";
import {
  POOL_SEARCH_JOIN_BACK_LABEL,
  POOL_SEARCH_JOIN_FULL_NOTICE,
  POOL_SEARCH_JOIN_PENDING_HEADING,
  POOL_SEARCH_JOIN_PENDING_MESSAGE,
  POOL_SEARCH_JOIN_TITLE,
  poolSearchJoinPreviewErrorCopy,
  readPoolSearchJoinPreview,
  type typePoolSearchJoinView,
} from "./poolSearchJoinFlow";
import { leagueAbbreviationsByKey } from "./poolsListState";

export type typePoolSearchJoinDialogProps = {
  /** The pool's internal id, from `/pools/search?poolId=`. Sent to pool-api as `targetPoolId`. */
  poolId: string;
  onClose: () => void;
  /** Navigates to the pool. The dialog never navigates itself. */
  onGoToPool: (poolId: string) => void;
};

type typeStep =
  | { step: "loading" }
  | { step: "failed"; message: string }
  | { step: "preview"; view: typePoolSearchJoinView; requestId: string }
  | { step: "pending"; view: typePoolSearchJoinView };

export const PoolSearchJoinDialog = (
  props: typePoolSearchJoinDialogProps,
): ReactNode => {
  const preview = usePoolJoinPreview();
  const join = usePoolJoin();
  const catalog = useLeagueCatalogGet({});
  const leagueAbbreviations = useMemo(
    () => leagueAbbreviationsByKey(catalog.data?.data?.sports ?? []),
    [catalog.data],
  );
  const [step, setStep] = useState<typeStep>({ step: "loading" });
  const hasStarted = useRef(false);

  const loadPreview = async (): Promise<void> => {
    let response;
    try {
      response = await preview.mutateAsync({ targetPoolId: props.poolId });
    } catch {
      setStep({ step: "failed", message: JOIN_OFFLINE_MESSAGE });
      return;
    }
    if (!response.data) {
      setStep({
        step: "failed",
        message: poolSearchJoinPreviewErrorCopy(response.error?.code ?? null),
      });
      return;
    }
    const outcome = readPoolSearchJoinPreview(response.data);
    if (outcome.kind === "goToPool") {
      props.onGoToPool(outcome.poolId);
      return;
    }
    setStep({
      step: "preview",
      view: outcome.view,
      requestId: newJoinRequestId(),
    });
  };

  // Empty deps on purpose: the route remounts this per open (`key={poolId}`)
  // and the ref guard keeps a StrictMode double effect to one request, so
  // mount is the only trigger there is.
  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;
    void loadPreview();
  }, []);

  const handleJoin = async (
    view: typePoolSearchJoinView,
    requestId: string,
  ): Promise<void> => {
    let response;
    try {
      response = await join.mutateAsync({
        targetPoolId: view.model.poolId,
        requestId,
      });
    } catch {
      showOfflineError();
      return;
    }
    if (!response.data) {
      showApiError(response);
      return;
    }
    if (response.data.membershipStatus === "active") {
      props.onGoToPool(response.data.poolId);
      return;
    }
    setStep({ step: "pending", view });
  };

  const backButton = (
    <Button
      variant="secondary"
      onClick={props.onClose}
      data-testid="pool-search-join-back"
    >
      {POOL_SEARCH_JOIN_BACK_LABEL}
    </Button>
  );

  let body: ReactNode;
  let footer: ReactNode = null;

  if (step.step === "loading") {
    body = <JoinPoolPreviewSkeleton />;
  } else if (step.step === "failed") {
    body = (
      <div data-testid="pool-search-join-failed">
        <InlineBanner variant="warning" message={step.message} />
      </div>
    );
    footer = backButton;
  } else if (step.step === "preview") {
    const { view, requestId } = step;
    if (view.availability.canJoin) {
      body = (
        <>
          <JoinPoolPreview
            model={view.model}
            leagueAbbreviations={leagueAbbreviations}
          />
          {view.isFull ? (
            <div data-testid="pool-search-join-full">
              <InlineBanner
                variant="warning"
                message={POOL_SEARCH_JOIN_FULL_NOTICE}
              />
            </div>
          ) : null}
        </>
      );
      footer = (
        <>
          <Button
            variant="secondary"
            onClick={props.onClose}
            data-testid="pool-search-join-not-now"
          >
            Not now
          </Button>
          <Button
            variant="primary"
            loading={join.isPending}
            onClick={() => void handleJoin(view, requestId)}
            data-testid="pool-search-join-commit"
          >
            Join pool
          </Button>
        </>
      );
    } else {
      body = (
        <>
          <JoinPoolPreview
            model={view.model}
            leagueAbbreviations={leagueAbbreviations}
          />
          <div data-testid="pool-search-join-notice">
            <InlineBanner
              variant="neutral"
              message={view.availability.notice ?? ""}
            />
          </div>
        </>
      );
      footer = backButton;
    }
  } else {
    body = (
      <>
        <p className="text-body-strong text-ink">
          {POOL_SEARCH_JOIN_PENDING_HEADING}
        </p>
        <JoinPoolPreview
          model={step.view.model}
          leagueAbbreviations={leagueAbbreviations}
        />
        <div data-testid="pool-search-join-pending">
          <InlineBanner
            variant="warning"
            message={POOL_SEARCH_JOIN_PENDING_MESSAGE}
          />
        </div>
      </>
    );
    footer = backButton;
  }

  return (
    <Dialog
      open={true}
      onOpenChange={(next) => {
        if (!next) props.onClose();
      }}
      title={POOL_SEARCH_JOIN_TITLE}
      autoFocus={false}
      footer={footer}
    >
      {body}
    </Dialog>
  );
};
